import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Wrench, ClipboardList, Monitor,
  MapPin, Shield, Bell, Settings, LogOut,
  Plane, ChevronRight, Activity, Calendar, GitBranch, CheckSquare, RefreshCw, FileText, FlaskConical, Calculator, Zap, Presentation, BookOpen, Globe, Award, GraduationCap, ArrowRightLeft, Plug, FileSearch, Smartphone, UserCheck, CalendarDays, Radio, MessageSquare,
} from "lucide-react";
import { useState, useEffect } from "react";
import DemoControls from "@/components/DemoControls";
import { swapRequestsApi, demoRequestsApi } from "@/api";

const navItems = [
  { to: "/dashboard", label: "Enterprise", icon: LayoutDashboard, roles: ["executive", "admin"] },
  { to: "/crew", label: "Crew Fatigue", icon: Users, roles: ["executive", "crew_scheduling", "admin", "pilot"] },
  { to: "/engineering", label: "Engineering", icon: Wrench, roles: ["executive", "engineering_manager", "admin", "ame"] },
  { to: "/camo", label: "CAMO", icon: ClipboardList, roles: ["executive", "engineering_manager", "admin"] },
  { to: "/back-office", label: "Back Office", icon: Monitor, roles: ["executive", "admin"] },
  { to: "/ground-ops", label: "Ground Ops", icon: MapPin, roles: ["executive", "admin"] },
  { to: "/safety", label: "Safety", icon: Shield, roles: ["executive", "safety_manager", "admin"] },
  { to: "/irrops", label: "IRROPS", icon: Activity, roles: ["executive", "crew_scheduling", "engineering_manager", "admin"] },
  { to: "/alerts", label: "Alerts", icon: Bell, roles: ["executive", "crew_scheduling", "engineering_manager", "safety_manager", "admin"] },
  { to: "/admin", label: "Admin", icon: Settings, roles: ["admin"] },
];

const rosterNavItems = [
  { to: "/roster/planner", label: "Roster Planner", icon: Calendar, roles: ["executive", "crew_scheduling", "admin"] },
  { to: "/roster/crew", label: "Crew Master", icon: Users, roles: ["executive", "crew_scheduling", "admin"] },
  { to: "/roster/pairings", label: "Pairings", icon: GitBranch, roles: ["executive", "crew_scheduling", "admin"] },
  { to: "/roster/compliance", label: "Compliance", icon: CheckSquare, roles: ["executive", "crew_scheduling", "safety_manager", "admin"] },
  { to: "/roster/recovery", label: "Recovery", icon: RefreshCw, roles: ["executive", "crew_scheduling", "admin"] },
  { to: "/roster/audit", label: "Audit Log", icon: FileText, roles: ["admin", "crew_scheduling"] },
  { to: "/roster/simulator", label: "Simulator", icon: FlaskConical, roles: ["executive", "crew_scheduling", "admin"] },
  { to: "/roster/disruption", label: "Disruption Engine", icon: Zap, roles: ["executive", "crew_scheduling", "admin"] },
  { to: "/roster/deployment",         label: "Crew Deployment",     icon: UserCheck,     roles: ["executive", "crew_scheduling", "admin"] },
  { to: "/roster/monthly-scheduler",  label: "Monthly Scheduler",   icon: CalendarDays,  roles: ["executive", "crew_scheduling", "admin"] },
];

const analyticsNavItems = [
  { to: "/staffing-engine",  label: "Staffing Engine",  icon: Calculator,       roles: ["executive", "crew_scheduling", "admin"] },
  { to: "/training-engine",  label: "Training Engine",  icon: GraduationCap,    roles: ["executive", "crew_scheduling", "admin", "safety_manager"] },
  { to: "/fairness-engine",  label: "Fairness & QoL",   icon: Award,            roles: ["executive", "crew_scheduling", "admin"] },
  { to: "/ops-console",      label: "Ops Console",      icon: Activity,         roles: ["executive", "crew_scheduling", "admin"] },
  { to: "/swap-requests",    label: "Swap Requests",    icon: ArrowRightLeft,   roles: ["executive", "crew_scheduling", "admin"], badgeKey: "swap" },
];

const flightOpsNavItems = [
  { to: "/flights",  label: "Live Flights", icon: Plane,  roles: ["executive", "crew_scheduling", "admin"] },
  { to: "/ops-map",  label: "Ops Map",      icon: Globe,  roles: ["executive", "crew_scheduling", "engineering_manager", "admin"] },
];

const demoNavItems = [
  { to: "/presentation",     label: "Demo Deck",          icon: Presentation, roles: ["executive", "admin"] },
  { to: "/fatigue-theatre",  label: "Fatigue Theatre",    icon: Activity,     roles: ["executive", "admin"] },
];

const complianceNavItems = [
  { to: "/frms",               label: "FRMS",              icon: Shield,     roles: ["executive", "crew_scheduling", "safety_manager", "admin"] },
  { to: "/integrations",       label: "Integrations",      icon: Plug,       roles: ["executive", "admin"] },
  { to: "/marketing-brochure", label: "Platform Brochure", icon: FileSearch, roles: ["executive", "admin"] },
];

const salesNavItems = [
  { to: "/pricing-calculator", label: "Pricing Calculator", icon: Calculator, roles: ["executive", "admin"] },
  { to: "/turnaround",         label: "Turnaround Tower",   icon: Radio,      roles: ["executive", "admin"], external: true },
  { to: "/demo-requests",      label: "Demo Requests",      icon: MessageSquare, roles: ["admin"], badgeKey: "demo" },
];

const pilotNavItems = [
  { to: "/my-sectors",  label: "My Sectors (SFR)", icon: Plane,   roles: ["pilot", "admin"] },
  { to: "/pilot-access", label: "Pilot App Access", icon: Smartphone, roles: ["admin"] },
];

const resourceNavItems = [
  { to: "/operations-manual", label: "Ops Manual", icon: BookOpen, roles: ["executive", "admin", "engineering_manager", "safety_manager"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [swapPending, setSwapPending] = useState(0);
  const [demoUnread, setDemoUnread] = useState(0);

  // Poll swap pending count every 30 s
  useEffect(() => {
    function fetchCount() {
      swapRequestsApi.pendingCount()
        .then(r => setSwapPending(r.data.count || 0))
        .catch(() => {});
    }
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, []);

  // Poll demo request unread count every 60 s (admin only)
  useEffect(() => {
    if (user?.role !== "admin") return;
    function fetchDemoCount() {
      demoRequestsApi.getUnreadCount()
        .then(r => setDemoUnread(r.data.count || 0))
        .catch(() => {});
    }
    fetchDemoCount();
    const id = setInterval(fetchDemoCount, 60000);
    return () => clearInterval(id);
  }, [user]);

  const visibleNav = navItems.filter(item =>
    item.roles.includes(user?.role) || user?.role === "admin"
  );
  const visibleRosterNav = rosterNavItems.filter(item =>
    item.roles.includes(user?.role) || user?.role === "admin"
  );

  const visibleAnalyticsNav = analyticsNavItems.filter(item =>
    item.roles.includes(user?.role) || user?.role === "admin"
  );

  const visibleFlightOpsNav = flightOpsNavItems.filter(item =>
    item.roles.includes(user?.role) || user?.role === "admin"
  );

  const visibleDemoNav = demoNavItems.filter(item =>
    item.roles.includes(user?.role) || user?.role === "admin"
  );

  const visibleComplianceNav = complianceNavItems.filter(item =>
    item.roles.includes(user?.role) || user?.role === "admin"
  );

  const visibleSalesNav = salesNavItems.filter(item =>
    item.roles.includes(user?.role) || user?.role === "admin"
  );

  const visibleResourceNav = resourceNavItems.filter(item =>
    item.roles.includes(user?.role) || user?.role === "admin"
  );

  const visiblePilotNav = pilotNavItems.filter(item =>
    item.roles.includes(user?.role) || user?.role === "admin"
  );

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <aside className={`sidebar flex flex-col transition-all duration-200 ${collapsed ? "w-16" : "w-56"} shrink-0 h-full overflow-hidden`} data-testid="sidebar">
        {/* Logo */}
        <div className="flex items-center gap-2 px-4 py-4 border-b border-border h-14">
          <div className="flex items-center justify-center w-7 h-7 bg-primary rounded-sm shrink-0">
            <Plane size={14} className="text-white" />
          </div>
          {!collapsed && (
            <div>
              <div className="text-xs font-black tracking-tight text-foreground font-heading leading-none">FATIGUE</div>
              <div className="text-xs font-black tracking-widest text-primary leading-none">SHIELD</div>
            </div>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="ml-auto text-muted-foreground hover:text-foreground transition-colors">
            <ChevronRight size={14} className={`transition-transform ${collapsed ? "" : "rotate-180"}`} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-2 overflow-y-auto min-h-0">
          {/* Fatigue Shield section */}
          {!collapsed && <div className="px-4 pt-2 pb-1 text-xs tracking-widest uppercase text-muted-foreground/50 font-bold">Fatigue Shield</div>}
          {visibleNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to} to={to}
              className={({ isActive }) => `nav-item flex items-center gap-3 px-4 py-2 text-sm cursor-pointer ${isActive ? "active text-primary" : "text-muted-foreground"}`}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={15} className="shrink-0" />
              {!collapsed && <span className="font-medium text-xs">{label}</span>}
            </NavLink>
          ))}

          {/* Crew Rostering section */}
          {visibleRosterNav.length > 0 && (
            <>
              {!collapsed && <div className="px-4 pt-4 pb-1 text-xs tracking-widest uppercase text-muted-foreground/50 font-bold border-t border-border/50 mt-2">Crew Rostering</div>}
              {collapsed && <div className="border-t border-border/50 mt-2 mb-1" />}
              {visibleRosterNav.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to} to={to}
                  className={({ isActive }) => `nav-item flex items-center gap-3 px-4 py-2 text-sm cursor-pointer ${isActive ? "active text-primary" : "text-muted-foreground"}`}
                  data-testid={`nav-roster-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon size={15} className="shrink-0" />
                  {!collapsed && <span className="font-medium text-xs">{label}</span>}
                </NavLink>
              ))}
            </>
          )}

          {/* Analytics section */}
          {visibleAnalyticsNav.length > 0 && (
            <>
              {!collapsed && <div className="px-4 pt-4 pb-1 text-xs tracking-widest uppercase text-muted-foreground/50 font-bold border-t border-border/50 mt-2">Analytics</div>}
              {collapsed && <div className="border-t border-border/50 mt-2 mb-1" />}
              {visibleAnalyticsNav.map(({ to, label, icon: Icon, badgeKey }) => {
                const badge = badgeKey === "swap" && swapPending > 0 ? swapPending : 0;
                return (
                  <NavLink
                    key={to} to={to}
                    className={({ isActive }) => `nav-item flex items-center gap-3 px-4 py-2 text-sm cursor-pointer ${isActive ? "active text-primary" : "text-muted-foreground"}`}
                    data-testid={`nav-analytics-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon size={15} className="shrink-0" />
                    {!collapsed && (
                      <span className="flex items-center gap-2 flex-1 font-medium text-xs">
                        {label}
                        {badge > 0 && (
                          <span className="ml-auto text-[9px] font-black px-1.5 py-0.5 bg-amber-500 text-black rounded-sm leading-none" data-testid="swap-pending-badge">
                            {badge}
                          </span>
                        )}
                      </span>
                    )}
                    {collapsed && badge > 0 && (
                      <span className="absolute right-2 top-1.5 w-2 h-2 rounded-full bg-amber-500" />
                    )}
                  </NavLink>
                );
              })}
            </>
          )}

          {/* Flight Ops section */}
          {visibleFlightOpsNav.length > 0 && (
            <>
              {!collapsed && <div className="px-4 pt-4 pb-1 text-xs tracking-widest uppercase text-muted-foreground/50 font-bold border-t border-border/50 mt-2">Flight Ops</div>}
              {collapsed && <div className="border-t border-border/50 mt-2 mb-1" />}
              {visibleFlightOpsNav.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to} to={to}
                  className={({ isActive }) => `nav-item flex items-center gap-3 px-4 py-2 text-sm cursor-pointer ${isActive ? "active text-primary" : "text-muted-foreground"}`}
                  data-testid={`nav-flights-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon size={15} className="shrink-0" />
                  {!collapsed && <span className="font-medium text-xs">{label}</span>}
                </NavLink>
              ))}
            </>
          )}

          {/* Demo Deck */}
          {visibleDemoNav.length > 0 && (
            <>
              {!collapsed && <div className="px-4 pt-4 pb-1 text-xs tracking-widest uppercase text-muted-foreground/50 font-bold border-t border-border/50 mt-2">Presentation</div>}
              {collapsed && <div className="border-t border-border/50 mt-2 mb-1" />}
              {visibleDemoNav.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to} to={to}
                  className={({ isActive }) => `nav-item flex items-center gap-3 px-4 py-2 text-sm cursor-pointer ${isActive ? "active text-primary" : "text-muted-foreground"}`}
                  data-testid={`nav-demo-deck`}
                >
                  <Icon size={15} className="shrink-0" />
                  {!collapsed && <span className="font-medium text-xs">{label}</span>}
                </NavLink>
              ))}
            </>
          )}

          {/* Compliance & Integrations */}
          {visibleComplianceNav.length > 0 && (
            <>
              {!collapsed && <div className="px-4 pt-4 pb-1 text-xs tracking-widest uppercase text-muted-foreground/50 font-bold border-t border-border/50 mt-2">Compliance</div>}
              {collapsed && <div className="border-t border-border/50 mt-2 mb-1" />}
              {visibleComplianceNav.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to} to={to}
                  className={({ isActive }) => `nav-item flex items-center gap-3 px-4 py-2 text-sm cursor-pointer ${isActive ? "active text-primary" : "text-muted-foreground"}`}
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon size={15} className="shrink-0" />
                  {!collapsed && <span className="font-medium text-xs">{label}</span>}
                </NavLink>
              ))}
            </>
          )}

          {/* Resources */}
          {visibleResourceNav.length > 0 && (
            <>
              {!collapsed && <div className="px-4 pt-4 pb-1 text-xs tracking-widest uppercase text-muted-foreground/50 font-bold border-t border-border/50 mt-2">Resources</div>}
              {collapsed && <div className="border-t border-border/50 mt-2 mb-1" />}
              {visibleResourceNav.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to} to={to}
                  className={({ isActive }) => `nav-item flex items-center gap-3 px-4 py-2 text-sm cursor-pointer ${isActive ? "active text-primary" : "text-muted-foreground"}`}
                  data-testid={`nav-ops-manual`}
                >
                  <Icon size={15} className="shrink-0" />
                  {!collapsed && <span className="font-medium text-xs">{label}</span>}
                </NavLink>
              ))}
            </>
          )}
          {/* Pilot SFR */}
          {visiblePilotNav.length > 0 && (
            <>
              {!collapsed && <div className="px-4 pt-4 pb-1 text-xs tracking-widest uppercase text-muted-foreground/50 font-bold border-t border-border/50 mt-2">Pilot</div>}
              {collapsed && <div className="border-t border-border/50 mt-2 mb-1" />}
              {visiblePilotNav.map(({ to, label, icon: Icon }) => (
                <NavLink key={to} to={to}
                  className={({ isActive }) => `nav-item flex items-center gap-3 px-4 py-2 text-sm cursor-pointer ${isActive ? "active text-primary" : "text-muted-foreground"}`}
                  data-testid="nav-my-sectors">
                  <Icon size={15} className="shrink-0" />
                  {!collapsed && <span className="font-medium text-xs">{label}</span>}
                </NavLink>
              ))}
            </>
          )}

          {/* Sales Tools */}
          {visibleSalesNav.length > 0 && (
            <>
              {!collapsed && <div className="px-4 pt-4 pb-1 text-xs tracking-widest uppercase text-muted-foreground/50 font-bold border-t border-border/50 mt-2">Sales Tools</div>}
              {collapsed && <div className="border-t border-border/50 mt-2 mb-1" />}
              {visibleSalesNav.map(({ to, label, icon: Icon, badgeKey }) => {
                const badge = badgeKey === "demo" && demoUnread > 0 ? demoUnread : 0;
                return (
                  <NavLink key={to} to={to}
                    className={({ isActive }) => `nav-item flex items-center gap-3 px-4 py-2 text-sm cursor-pointer ${isActive ? "active text-primary" : "text-muted-foreground"}`}
                    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="relative shrink-0">
                      <Icon size={15} />
                      {collapsed && badge > 0 && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-orange-500" />
                      )}
                    </div>
                    {!collapsed && (
                      <span className="flex items-center gap-2 flex-1 font-medium text-xs">
                        {label}
                        {badge > 0 && (
                          <span className="ml-auto text-[9px] font-black px-1.5 py-0.5 bg-orange-500 text-white rounded-sm leading-none" data-testid="demo-requests-badge">
                            {badge}
                          </span>
                        )}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        {/* User + Demo Controls */}
        <div className="border-t border-border p-3">
          {/* Demo Controls — admin/executive only */}
          {(user?.role === "admin" || user?.role === "executive") && !collapsed && (
            <div className="mb-2">
              <DemoControls />
            </div>
          )}
          {!collapsed && (
            <div className="flex items-center gap-2 mb-2 px-1">
              <div className="w-7 h-7 rounded-sm bg-secondary flex items-center justify-center text-xs font-bold text-primary shrink-0">
                {user?.name?.[0] || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-foreground truncate">{user?.name}</div>
                <div className="text-xs text-muted-foreground truncate capitalize">{user?.role?.replace(/_/g, " ")}</div>
              </div>
              {/* Notification bell — admin demo requests */}
              {user?.role === "admin" && (
                <NavLink to="/demo-requests" className="relative ml-1 p-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="demo-requests-bell">
                  <Bell size={14} />
                  {demoUnread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 bg-orange-500 rounded-full text-[8px] font-black text-white flex items-center justify-center px-0.5 leading-none" data-testid="bell-badge-count">
                      {demoUnread > 9 ? "9+" : demoUnread}
                    </span>
                  )}
                </NavLink>
              )}
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-2 py-2 text-xs text-muted-foreground hover:text-destructive transition-colors rounded-sm"
            data-testid="logout-button"
          >
            <LogOut size={14} className="shrink-0" />
            {!collapsed && "Sign Out"}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto" data-testid="main-content">
        <Outlet />
      </main>
    </div>
  );
}
